import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { formatAppDate, formatAppDateRange } from "../../utils/dateFormat";
import { formatPhoneCompact } from "../../utils/phoneUtils";
import api from "../../services/axios";
import RecruiterSsoDropdown from "../../components/custom/RecruiterSsoDropdown";
import {
  FileText, Globe, Calendar, ChevronDown, Building2,
  TrendingUp, Target, XCircle, Briefcase, Search, X,
  ChevronLeft, ChevronRight,
} from "lucide-react";

function tenantQuery(ids) {
  const list = ids instanceof Set ? Array.from(ids).filter(Boolean) : [];
  return list.length ? { tenantIds: list.join(",") } : {};
}

function createdByLabel(pos) {
  const name = String(pos?.createdBy || "").trim();
  const email = String(pos?.createdByEmail || "").trim();
  return name || email || "—";
}

function toIsoDate(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const card = { background: "var(--ds-card)", border: "1px solid var(--ds-border)", borderRadius: 12, boxShadow: "var(--ds-shadow-md)" };
const card2 = { background: "var(--ds-card2)", border: "1px solid var(--ds-border)", borderRadius: 12 };
const inp = {
  background: "var(--ds-input-bg)", border: "1.5px solid var(--ds-border)",
  borderRadius: 8, color: "var(--ds-text)", padding: "8px 12px",
  fontSize: 13, outline: "none", width: "100%", fontFamily: "inherit",
};
const labelStyle = { fontSize: 12, fontWeight: 700, color: "var(--ds-text2)", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 };
const btnOutline = {
  background: "var(--ds-input-bg)", border: "1.5px solid var(--ds-border)",
  color: "var(--ds-text2)", borderRadius: 8, padding: "8px 14px",
  fontWeight: 600, cursor: "pointer", fontSize: 12, fontFamily: "inherit",
};

const DATE_PRESETS = [
  { label: "Today", days: 0 },
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
];

const COUNTRY_OPTIONS = [
  { id: "all", name: "All Countries" },
  { id: "india", name: "India" },
  { id: "usa", name: "United States" },
  { id: "uk", name: "United Kingdom" },
  { id: "uae", name: "UAE" },
  { id: "canada", name: "Canada" },
];

/* JD Analytics funnel — 6-stage hiring flow with realtime /analytics/summary fields */
const FUNNEL_STAGE_META = [
  { key: "positionCreated", label: "Positions", color: "#1e3a8a" },
  { key: "profileReceived", label: "Profile Pooled", color: "#1d4ed8" },
  { key: "processed", label: "Processed", color: "#0284c7" },
  { key: "aiShortlisted", label: "AI Shortlisted", color: "#0d9488" },
  { key: "selected", label: "Selected", color: "#7c3aed" },
  { key: "rejected", label: "Rejected", color: "#dc2626" },
];

const EMPTY_FUNNEL = {
  positionCreated: 0,
  profileReceived: 0,
  processed: 0,
  aiShortlisted: 0,
  selected: 0,
  rejected: 0,
};

function normalizeStatus(status) {
  return String(status || "").trim().toLowerCase();
}

function isRejectedStatus(status) {
  const s = normalizeStatus(status);
  return s === "reject" || s === "rejected" || s === "rejection";
}

function HiringReachFunnel({ stages, onStageClick }) {
  const W = 1000;
  const H = 148;
  const n = Math.max(stages.length, 1);
  const segW = W / n;
  const minHeightRatio = 0.78;
  const bottomY = (x) => H - H * (1 - minHeightRatio) * (x / W);

  return (
    <div style={{ position: "relative", width: "100%", height: 148 }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        style={{ width: "100%", height: 148, display: "block" }}
        role="img"
        aria-label="JD recruitment funnel"
      >
        {stages.map((stage, i) => {
          const x0 = i * segW;
          const x1 = (i + 1) * segW;
          const y0b = bottomY(x0);
          const y1b = bottomY(x1);
          const points = `${x0},0 ${x1},0 ${x1},${y1b} ${x0},${y0b}`;
          return (
            <polygon
              key={stage.key || stage.label}
              points={points}
              fill={stage.color}
              stroke="rgba(255,255,255,0.35)"
              strokeWidth="1"
              style={{ transition: "fill 0.3s ease" }}
            />
          );
        })}
      </svg>
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        {stages.map((stage, i) => {
          const midX = (i + 0.5) * segW;
          const visibleH = bottomY(midX);
          const heightPct = (visibleH / H) * 100;
          const clickable = typeof onStageClick === "function";
          return (
            <div
              key={stage.key || stage.label}
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : undefined}
              title={clickable ? `View ${stage.label} details` : undefined}
              onClick={clickable ? () => onStageClick(stage) : undefined}
              onKeyDown={clickable ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onStageClick(stage);
                }
              } : undefined}
              style={{
                position: "absolute",
                left: `${(i / n) * 100}%`,
                width: `${100 / n}%`,
                top: 0,
                height: `${heightPct}%`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "6px 6px",
                color: "#fff",
                textAlign: "center",
                boxSizing: "border-box",
                overflow: "hidden",
                pointerEvents: clickable ? "auto" : "none",
                cursor: clickable ? "pointer" : "default",
              }}
            >
              <div style={{
                fontSize: n > 4 ? 9 : 11,
                fontWeight: 700,
                lineHeight: 1.15,
                marginBottom: 4,
                textShadow: "0 1px 2px rgba(0,0,0,0.35)",
                maxWidth: "96%",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}>
                {stage.label}
              </div>
              <div style={{
                fontSize: n > 4 ? 18 : 24,
                fontWeight: 900,
                lineHeight: 1,
                textShadow: "0 1px 2px rgba(0,0,0,0.35)",
              }}>
                {stage.value}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ConversionCard({ icon: Icon, iconBg, title, fromVal, toVal }) {
  return (
    <div style={{ ...card2, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 180 }}>
      <div style={{ width: 36, height: 36, borderRadius: "50%", background: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={16} color="#fff" />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--ds-text2)", marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 13, fontWeight: 800, color: "var(--ds-text)" }}>
          {fromVal} → {toVal}
        </div>
      </div>
    </div>
  );
}

function craftStatusKey(position) {
  const lower = String(position?.status ?? position?.Status ?? "Pending").trim().toLowerCase();
  if (lower === "stacked") return "Stacked";
  if (lower === "close" || lower === "closed") return "Closed";
  return "Pending";
}

function roundAgingDays(value) {
  if (value == null || Number.isNaN(Number(value))) return null;
  const num = Number(value);
  const frac = num - Math.floor(num);
  return frac <= 0.5 ? Math.floor(num) : Math.ceil(num);
}

function fmtAgingDays(value) {
  const days = roundAgingDays(value);
  return days == null ? "—" : `${days}d`;
}

function positionAgingDays(position) {
  if (!position?.createdAt) return null;
  const start = new Date(position.createdAt).getTime();
  if (Number.isNaN(start)) return null;
  const status = craftStatusKey(position);
  let end = Date.now();
  if (status === "Closed") {
    const closedRaw = position.closedAt || position.updatedAt;
    if (closedRaw) {
      const closed = new Date(closedRaw).getTime();
      if (!Number.isNaN(closed)) end = closed;
    }
  }
  const days = (end - start) / 86400000;
  return days < 0 ? null : days;
}

const CRAFT_STATUS_STYLE = {
  Pending: { bg: "rgba(0,65,194,0.1)", color: "#0041C2", dot: "#0041C2" },
  Stacked: { bg: "rgba(16,185,129,0.12)", color: "#059669", dot: "#10B981" },
  Closed: { bg: "rgba(100,116,139,0.12)", color: "#64748b", dot: "#94a3b8" },
};

const actBtn = {
  width: 28, height: 28, borderRadius: 7, border: "1px solid var(--ds-border)",
  background: "var(--ds-card)", color: "var(--ds-text2)", cursor: "pointer",
  display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0,
};
const POPUP_PAGE_SIZE = 8;

const STAGE_POPUP_HINT = {
  positionCreated: "Positions in the current JD filter.",
  profileReceived: "Ranked candidate profiles pooled for the current JD filter.",
  processed: "Candidates successfully processed in stack ranking (excludes extraction failures).",
  aiShortlisted: "Candidates scored ≥ 50% by AI ranking.",
  selected: "Candidates marked Selected via Selection Status (AI Shortlisted are selected by default).",
  rejected: "Candidates marked Rejected via Selection Status on Profile Pooled / Processed.",
};

const CANDIDATE_FUNNEL_KEYS = new Set([
  "profileReceived", "processed", "aiShortlisted", "selected", "rejected",
]);

const CANDIDATES_PAGE_SIZE = 8;

function phoneDigits(value) {
  return String(value || "").replace(/\D/g, "");
}

function normalizeRankingCandidate(raw, position) {
  const score = Number(raw.totalScore ?? raw.total_score ?? raw.match_score ?? 0);
  return {
    rank: raw.rank,
    name: raw.candidateName || raw.name || "Unknown",
    email: raw.email || raw.candidateEmail || raw.candidate_email || "—",
    mobile: raw.mobile || raw.phone || raw.candidatePhone || "",
    total_score: Number.isFinite(score) ? Math.round(score) : 0,
    resume_id: raw.resumeId || raw.resume_id || raw.id,
    positionId: position?.positionId,
    positionName: position?.positionName,
  };
}

function buildWaLatestByPhone(messages) {
  const map = new Map();
  (messages || []).forEach((msg) => {
    const key = phoneDigits(msg.phoneNumber);
    if (!key) return;
    const prev = map.get(key);
    if (!prev) {
      map.set(key, msg);
      return;
    }
    const prevT = new Date(prev.updatedAt || prev.createdAt || 0).getTime();
    const nextT = new Date(msg.updatedAt || msg.createdAt || 0).getTime();
    if (nextT >= prevT) map.set(key, msg);
  });
  return map;
}

function matchWaMessage(mobile, waMap) {
  const key = phoneDigits(mobile);
  if (!key || !waMap?.size) return null;
  if (waMap.has(key)) return waMap.get(key);
  const last10 = key.slice(-10);
  for (const [k, msg] of waMap.entries()) {
    if (k.slice(-10) === last10) return msg;
  }
  return null;
}

function resolveCandidateWaStatus(candidate, waMap) {
  const msg = matchWaMessage(candidate.mobile, waMap);
  if (!msg) return "not_sent";
  if (msg.noticePeriod) return "completed";
  if (msg.status === "completed") return "completed";
  if (msg.status === "awaiting-details") return "awaiting-details";
  if (msg.status === "interested" || msg.userResponse === "Yes") return "awaiting-details";
  if (msg.status === "not-interested" || msg.status === "not_interested" || msg.userResponse === "No") {
    return "not-interested";
  }
  return msg.status || "processing";
}

function isFailedRankingCandidate(c) {
  const name = String(c?.name || "");
  return /^extraction\s*error/i.test(name);
}

const JD_DECISION_STORAGE_KEY = "reachout.jdAnalytics.candidateDecisions";

function candidateDecisionKey(c) {
  return `${c?.positionId ?? ""}::${c?.resume_id ?? c?.email ?? ""}::${phoneDigits(c?.mobile)}`;
}

function readStoredDecisions() {
  try {
    const raw = localStorage.getItem(JD_DECISION_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeStoredDecision(key, value) {
  const all = readStoredDecisions();
  if (!value) delete all[key];
  else all[key] = value;
  localStorage.setItem(JD_DECISION_STORAGE_KEY, JSON.stringify(all));
}

/** Selected | Rejected | '' — AI shortlisted (≥50%) default to Selected unless explicitly rejected. */
function resolveCandidateDecision(c) {
  if (!c || isFailedRankingCandidate(c)) return "";
  const stored = readStoredDecisions()[candidateDecisionKey(c)];
  if (stored === "selected" || stored === "rejected") return stored;
  const sl = String(c.shortlistStatus || "").toLowerCase();
  if (sl === "shortlisted") return "selected";
  if (sl === "not-shortlisted" || sl === "not_shortlisted") return "rejected";
  if (Number(c.total_score) >= 50) return "selected";
  if (c.waStatus === "not-interested" || c.waStatus === "not_interested") return "rejected";
  if (["completed", "awaiting-details", "interested"].includes(c.waStatus)) return "selected";
  return "";
}

function applyStageCandidateFilter(candidates, stageKey) {
  const list = Array.isArray(candidates) ? candidates : [];
  switch (stageKey) {
    case "profileReceived":
      return list;
    case "processed":
      return list.filter((c) => !isFailedRankingCandidate(c));
    case "aiShortlisted":
      return list.filter((c) => !isFailedRankingCandidate(c) && Number(c.total_score) >= 50);
    case "selected":
      return list.filter((c) => resolveCandidateDecision(c) === "selected");
    case "rejected":
      return list.filter((c) => resolveCandidateDecision(c) === "rejected");
    default:
      return list;
  }
}

function countCandidateFunnelStages(candidates) {
  return {
    profileReceived: applyStageCandidateFilter(candidates, "profileReceived").length,
    processed: applyStageCandidateFilter(candidates, "processed").length,
    aiShortlisted: applyStageCandidateFilter(candidates, "aiShortlisted").length,
    selected: applyStageCandidateFilter(candidates, "selected").length,
    rejected: applyStageCandidateFilter(candidates, "rejected").length,
  };
}

async function fetchMergedRankingCandidates(positions) {
  const list = Array.isArray(positions) ? positions : [];
  if (list.length === 0) return [];
  const bundles = await Promise.all(
    list.map(async (position) => {
      const id = position.positionId;
      const [rankRes, waRes] = await Promise.all([
        api.get(`/stack-ranking/position/${id}/results`).catch(() => ({ data: [] })),
        api.get(`/whatsapp-messages/position/${id}`).catch(() => ({ data: [] })),
      ]);
      const waMap = buildWaLatestByPhone(Array.isArray(waRes.data) ? waRes.data : []);
      const rows = Array.isArray(rankRes.data) ? rankRes.data : [];
      return rows.map((raw) => {
        const base = normalizeRankingCandidate(raw, position);
        const waMsg = matchWaMessage(base.mobile, waMap);
        return {
          ...base,
          waStatus: resolveCandidateWaStatus(base, waMap),
          waMessageId: waMsg?.id ?? waMsg?.messageId ?? null,
          shortlistStatus: waMsg?.shortlistStatus ?? waMsg?.shortlist_status ?? null,
        };
      });
    })
  );
  let merged = bundles.flat();
  merged.sort((a, b) => Number(b.total_score) - Number(a.total_score));
  const seen = new Set();
  merged = merged.filter((c) => {
    const key = c.resume_id != null ? `r:${c.resume_id}` : `e:${c.email}|${c.mobile}|${c.positionId}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return merged.map((c, idx) => ({ ...c, rank: idx + 1 }));
}

function FunnelCandidatesPopup({ positions, stageKey, title, subtitle, accentColor, stageValue, onClose, onDecisionChange }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [page, setPage] = useState(1);
  const [decisionBusyKey, setDecisionBusyKey] = useState("");
  const [pendingDecision, setPendingDecision] = useState(null);
  /* Selection Status on all candidate stages; editable only on Profile Pooled + Processed */
  const showSelectionStatus = CANDIDATE_FUNNEL_KEYS.has(stageKey);
  const canEditSelection = ["profileReceived", "processed"].includes(stageKey);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (pendingDecision) setPendingDecision(null);
      else onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose, pendingDecision]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      setPage(1);
      const list = Array.isArray(positions) ? positions : [];
      if (list.length === 0) {
        if (!cancelled) {
          setCandidates([]);
          setLoading(false);
        }
        return;
      }
      try {
        let merged = await fetchMergedRankingCandidates(list);
        merged = applyStageCandidateFilter(merged, stageKey);
        if (!cancelled) setCandidates(merged);
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.message || e?.message || "Failed to load candidates.");
          setCandidates([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [positions, stageKey]);

  useEffect(() => { setPage(1); }, [candidates]);

  const total = candidates.length;
  const totalPages = Math.max(1, Math.ceil(total / CANDIDATES_PAGE_SIZE) || 1);
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = total === 0 ? 0 : (safePage - 1) * CANDIDATES_PAGE_SIZE + 1;
  const end = Math.min(safePage * CANDIDATES_PAGE_SIZE, total);
  const pageRows = candidates.slice((safePage - 1) * CANDIDATES_PAGE_SIZE, safePage * CANDIDATES_PAGE_SIZE);

  const handleCandidateDecision = async (candidate, nextDecision) => {
    const key = candidateDecisionKey(candidate);
    const value = nextDecision === "selected" || nextDecision === "rejected" ? nextDecision : "";
    setDecisionBusyKey(key);
    writeStoredDecision(key, value);
    setCandidates((prev) => {
      const updated = prev.map((row) => (
        candidateDecisionKey(row) === key
          ? { ...row, shortlistStatus: value === "selected" ? "shortlisted" : value === "rejected" ? "not-shortlisted" : row.shortlistStatus }
          : row
      ));
      return applyStageCandidateFilter(updated, stageKey);
    });
    if (candidate.waMessageId && value) {
      try {
        await api.patch(`/whatsapp-messages/${candidate.waMessageId}/shortlist-status`, {
          shortlistStatus: value === "selected" ? "shortlisted" : "not-shortlisted",
        });
      } catch {
        /* local decision still applies for JD Analytics funnel */
      }
    }
    setDecisionBusyKey("");
    if (typeof onDecisionChange === "function") onDecisionChange();
  };

  const requestCandidateDecision = (candidate, nextDecision) => {
    const current = resolveCandidateDecision(candidate);
    const value = nextDecision === "selected" || nextDecision === "rejected" ? nextDecision : "";
    if (value === current) return;
    if (value === "selected" || value === "rejected") {
      setPendingDecision({ candidate, nextDecision: value });
      return;
    }
    handleCandidateDecision(candidate, value);
  };

  const confirmPendingDecision = async () => {
    if (!pendingDecision) return;
    const { candidate, nextDecision } = pendingDecision;
    setPendingDecision(null);
    await handleCandidateDecision(candidate, nextDecision);
  };
  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = new Set([1, totalPages, safePage, safePage - 1, safePage + 1].filter((p) => p >= 1 && p <= totalPages));
    return [...pages].sort((a, b) => a - b);
  }, [totalPages, safePage]);

  const accent = accentColor || "#0041C2";
  const thStyle = {
    padding: "8px 6px", fontSize: 12, fontWeight: 700, color: "var(--ds-text)",
    background: "var(--ds-surface)", borderBottom: "2px solid var(--ds-border)", whiteSpace: "nowrap",
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <>
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title || "Candidates"}
      style={{
        position: "fixed",
        top: 0, left: 0, right: 0, bottom: 0,
        width: "100vw", height: "100vh",
        zIndex: 12000,
        background: "rgba(15,23,42,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px",
        paddingLeft: "calc(var(--app-sidebar-width, 0px) + 16px)",
        boxSizing: "border-box",
      }}
      onClick={() => { if (!pendingDecision) onClose(); }}
    >
      <div
        style={{
          ...card,
          padding: 0,
          width: "min(980px, calc(100vw - var(--app-sidebar-width, 0px) - 32px))",
          maxWidth: "calc(100vw - var(--app-sidebar-width, 0px) - 32px)",
          maxHeight: "min(85vh, calc(100vh - 48px))",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          margin: "0 auto",
          boxSizing: "border-box",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          padding: "14px 18px", borderBottom: "1px solid var(--ds-border)",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexShrink: 0,
        }}>
          <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 10, height: 10, borderRadius: 3, background: accent, flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                <div style={{ fontWeight: 900, fontSize: 15, color: "var(--ds-text)" }}>{title || "Candidates"}</div>
                <div style={{ fontSize: 18, fontWeight: 900, color: accent }}>
                  {loading ? (stageValue != null ? stageValue : "…") : candidates.length}
                </div>
              </div>
              <div style={{ fontSize: 12, color: "var(--ds-muted)", marginTop: 2 }}>
                {subtitle || `${total} candidates`}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => { if (pendingDecision) setPendingDecision(null); else onClose(); }}
            style={{
              border: "none", background: "var(--ds-card2)", width: 28, height: 28, borderRadius: 8,
              cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center",
              color: "var(--ds-text2)", flexShrink: 0,
            }}
          >
            <X size={14} />
          </button>
        </div>


        <div style={{ overflow: "auto", flex: 1, minHeight: 0, padding: "0 16px 8px" }}>
          {loading ? (
            <div style={{ padding: 28, textAlign: "center", color: "var(--ds-muted)", fontSize: 13, fontWeight: 600 }}>
              Loading candidates…
            </div>
          ) : error ? (
            <div style={{ padding: 28, textAlign: "center", color: "#ef4444", fontSize: 13, fontWeight: 600 }}>
              {error}
            </div>
          ) : total === 0 ? (
            <div style={{ padding: 28, textAlign: "center", color: "var(--ds-muted)", fontSize: 13, fontWeight: 600 }}>
              No candidates found for this stage.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: 56, textAlign: "center" }}>Rank</th>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Phone</th>
                  <th style={{ ...thStyle, width: 72, textAlign: "center" }}>Score</th>
                  {showSelectionStatus && (
                    <th style={{ ...thStyle, width: 130, textAlign: "center" }}>Selection Status</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((c, index) => {
                  const key = c.resume_id || `${c.email}-${c.mobile}-${c.rank}-${index}`;
                  return (
                    <tr key={key} style={{ borderBottom: "1px solid var(--ds-border)" }}>
                      <td style={{ textAlign: "center", padding: "6px 4px" }}>
                        <span style={{
                          borderRadius: 4, padding: "1px 6px", fontSize: "0.65rem", fontWeight: 700,
                          background: c.rank <= 3 ? "#28a745" : "rgb(89,144,225)", color: "white", display: "inline-block",
                        }}>
                          {c.rank}
                        </span>
                      </td>
                      <td style={{ padding: "6px 4px", color: "var(--ds-text)" }}>{c.name}</td>
                      <td style={{ padding: "6px 4px", color: "var(--ds-text2)" }}>{c.email}</td>
                      <td style={{ padding: "6px 4px", color: "var(--ds-text2)" }}>
                        {formatPhoneCompact(c.mobile) || c.mobile || "N/A"}
                      </td>
                      <td style={{ textAlign: "center", padding: "6px 4px" }}>
                        <span style={{
                          borderRadius: 6, padding: "4px 8px", fontSize: "0.8rem",
                          background: "rgb(89,144,225)", color: "white", fontWeight: 600,
                          display: "inline-block", minWidth: 40, textAlign: "center",
                        }}>
                          {c.total_score}%
                        </span>
                      </td>
                      {showSelectionStatus && (() => {
                        const dKey = candidateDecisionKey(c);
                        const decision = resolveCandidateDecision(c);
                        const busy = decisionBusyKey === dKey;
                        const statusLabel = decision === "selected"
                          ? "Selected"
                          : decision === "rejected"
                            ? "Rejected"
                            : "—";
                        const statusStyles = decision === "rejected"
                          ? { border: "1px solid #dc2626", background: "rgba(220,38,38,0.1)", color: "#dc2626" }
                          : decision === "selected"
                            ? { border: "1px solid #16a34a", background: "rgba(22,163,74,0.12)", color: "#15803d" }
                            : { border: "1px solid var(--ds-border)", background: "var(--ds-card2)", color: "var(--ds-muted)" };
                        if (!canEditSelection) {
                          return (
                            <td style={{ textAlign: "center", padding: "6px 4px" }}>
                              <span style={{
                                display: "inline-block", borderRadius: 8, padding: "5px 10px",
                                fontSize: 11, fontWeight: 700, minWidth: 96, ...statusStyles,
                              }}>
                                {statusLabel}
                              </span>
                            </td>
                          );
                        }
                        return (
                          <td style={{ textAlign: "center", padding: "6px 4px" }} onClick={(e) => e.stopPropagation()}>
                            <select
                              value={decision || ""}
                              disabled={busy || !!pendingDecision}
                              onChange={(e) => requestCandidateDecision(c, e.target.value)}
                              style={{
                                borderRadius: 8, padding: "5px 8px", fontSize: 11, fontWeight: 700,
                                border: statusStyles.border,
                                background: statusStyles.background,
                                color: statusStyles.color,
                                fontFamily: "inherit",
                                cursor: busy || pendingDecision ? "wait" : "pointer",
                                minWidth: 96,
                              }}
                            >
                              <option value="">Select…</option>
                              <option value="selected">Selected</option>
                              <option value="rejected">Rejected</option>
                            </select>
                          </td>
                        );
                      })()}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div style={{
          padding: "10px 16px", borderTop: "1px solid var(--ds-border)",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
          flexShrink: 0, background: "var(--ds-card)",
        }}>
          <span style={{ fontSize: 12, color: "var(--ds-muted)", fontWeight: 600 }}>
            {loading ? "…" : total === 0 ? "Showing 0 of 0" : `Showing ${start}–${end} of ${total}`}
          </span>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <button
              type="button"
              disabled={safePage <= 1 || total === 0}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              style={{ ...actBtn, opacity: safePage <= 1 || total === 0 ? 0.4 : 1 }}
            >
              <ChevronLeft size={14} />
            </button>
            {pageNumbers.map((p, i) => {
              const prev = pageNumbers[i - 1];
              const showEllipsis = prev != null && p - prev > 1;
              return (
                <React.Fragment key={p}>
                  {showEllipsis && <span style={{ fontSize: 12, color: "var(--ds-muted)", padding: "0 2px" }}>…</span>}
                  <button
                    type="button"
                    onClick={() => setPage(p)}
                    style={{
                      minWidth: 28, height: 28, borderRadius: 7, border: "none", cursor: "pointer",
                      background: p === safePage ? accent : "var(--ds-card2)",
                      color: p === safePage ? "#fff" : "var(--ds-text2)",
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      fontSize: 12, fontWeight: 800, fontFamily: "inherit",
                    }}
                  >
                    {p}
                  </button>
                </React.Fragment>
              );
            })}
            <button
              type="button"
              disabled={safePage >= totalPages || total === 0}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              style={{ ...actBtn, opacity: safePage >= totalPages || total === 0 ? 0.4 : 1 }}
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>

    {pendingDecision && (
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Confirm candidate decision"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 12100,
          background: "rgba(15,23,42,0.55)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16,
          boxSizing: "border-box",
        }}
        onClick={() => setPendingDecision(null)}
      >
        <div
          style={{
            background: "var(--ds-card)",
            borderRadius: 12,
            width: "90%",
            maxWidth: 420,
            boxShadow: "var(--ds-shadow-md)",
            border: "1px solid var(--ds-border)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{
            padding: "14px 18px", borderBottom: "1px solid var(--ds-border)",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
          }}>
            <div style={{ fontWeight: 800, fontSize: 14, color: "var(--ds-text)" }}>
              Confirm {pendingDecision.nextDecision === "rejected" ? "Rejection" : "Selection"}
            </div>
            <button
              type="button"
              onClick={() => setPendingDecision(null)}
              style={{
                border: "none", background: "var(--ds-card2)", width: 28, height: 28, borderRadius: 8,
                cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center",
                color: "var(--ds-text2)", flexShrink: 0,
              }}
            >
              <X size={14} />
            </button>
          </div>
          <div style={{ padding: "18px 20px", textAlign: "center" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ds-text)", marginBottom: 8 }}>
              {pendingDecision.nextDecision === "rejected"
                ? `Mark ${pendingDecision.candidate?.name || "this candidate"} as Rejected?`
                : `Mark ${pendingDecision.candidate?.name || "this candidate"} as Selected?`}
            </div>
            <div style={{ fontSize: 12, color: "var(--ds-muted)", lineHeight: 1.45 }}>
              {pendingDecision.nextDecision === "rejected"
                ? "They will appear under the Rejected funnel tile."
                : "They will appear under the Selected funnel tile."}
            </div>
          </div>
          <div style={{
            padding: "12px 18px", borderTop: "1px solid var(--ds-border)",
            display: "flex", justifyContent: "flex-end", gap: 8,
          }}>
            <button
              type="button"
              onClick={() => setPendingDecision(null)}
              style={{
                borderRadius: 8, padding: "7px 14px", border: "1px solid var(--ds-border)",
                background: "transparent", color: "var(--ds-text)", cursor: "pointer",
                fontSize: 13, fontWeight: 600, fontFamily: "inherit",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmPendingDecision}
              style={{
                borderRadius: 8, padding: "7px 14px", border: "none",
                background: pendingDecision.nextDecision === "rejected" ? "#dc2626" : "#16a34a",
                color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit",
              }}
            >
              {pendingDecision.nextDecision === "rejected" ? "Confirm Rejected" : "Confirm Selected"}
            </button>
          </div>
        </div>
      </div>
    )}
    </>,
    document.body
  );
}

/** Craft JD–style position table popup for Position/Role (paginated, no Actions). */
function FunnelStagePositionsPopup({ rows, title, subtitle, accentColor, stageValue, onClose }) {
  const [page, setPage] = useState(1);

  useEffect(() => { setPage(1); }, [rows, title]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const total = Array.isArray(rows) ? rows.length : 0;
  const totalPages = Math.max(1, Math.ceil(total / POPUP_PAGE_SIZE) || 1);
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = total === 0 ? 0 : (safePage - 1) * POPUP_PAGE_SIZE + 1;
  const end = Math.min(safePage * POPUP_PAGE_SIZE, total);
  const pageRows = (rows || []).slice((safePage - 1) * POPUP_PAGE_SIZE, safePage * POPUP_PAGE_SIZE);

  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = new Set([1, totalPages, safePage, safePage - 1, safePage + 1].filter((p) => p >= 1 && p <= totalPages));
    return [...pages].sort((a, b) => a - b);
  }, [totalPages, safePage]);

  const th = {
    textAlign: "left", fontSize: 10, fontWeight: 800, letterSpacing: "0.04em",
    color: "var(--ds-muted)", textTransform: "uppercase", padding: "10px 12px",
    borderBottom: "1px solid var(--ds-border)", whiteSpace: "nowrap", background: "var(--ds-card2)",
  };
  const td = {
    padding: "12px", borderBottom: "1px solid var(--ds-border)", fontSize: 13,
    color: "var(--ds-text)", verticalAlign: "middle",
  };
  const accent = accentColor || "#0041C2";

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title || "Funnel stage positions"}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 12000,
        background: "rgba(15,23,42,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        /* Keep dialog in the main content area — do not cover / sit under the sidebar */
        padding: "24px 16px",
        paddingLeft: "calc(var(--app-sidebar-width, 0px) + 16px)",
        boxSizing: "border-box",
      }}
      onClick={onClose}
    >
      <div
        style={{
          ...card,
          padding: 0,
          width: "min(1080px, calc(100vw - var(--app-sidebar-width, 0px) - 32px))",
          maxWidth: "calc(100vw - var(--app-sidebar-width, 0px) - 32px)",
          maxHeight: "min(85vh, calc(100vh - 48px))",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          margin: "0 auto",
          boxSizing: "border-box",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          padding: "14px 18px", borderBottom: "1px solid var(--ds-border)",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexShrink: 0,
        }}>
          <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 10, height: 10, borderRadius: 3, background: accent, flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                <div style={{ fontWeight: 900, fontSize: 15, color: "var(--ds-text)" }}>{title || "Positions"}</div>
                {stageValue != null && (
                  <div style={{ fontSize: 18, fontWeight: 900, color: accent }}>{stageValue}</div>
                )}
              </div>
              <div style={{ fontSize: 12, color: "var(--ds-muted)", marginTop: 2 }}>
                {subtitle || (total === 1 ? "1 position in current JD filter" : `${total} positions in current JD filter`)}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none", background: "var(--ds-card2)", width: 28, height: 28, borderRadius: 8,
              cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center",
              color: "var(--ds-text2)", flexShrink: 0,
            }}
          >
            <X size={14} />
          </button>
        </div>

        <div style={{ overflow: "auto", flex: 1, minHeight: 0 }}>
          {total === 0 ? (
            <div style={{ padding: 28, textAlign: "center", color: "var(--ds-muted)", fontSize: 13, fontWeight: 600 }}>
              No positions in the current filter.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "auto" }}>
              <thead>
                <tr>
                  <th style={{ ...th, width: 52 }}>S.No</th>
                  <th style={th}>Job Role</th>
                  <th style={th}>Client Name</th>
                  <th style={th}>Job Code</th>
                  <th style={th}>Created By</th>
                  <th style={th}>Days</th>
                  <th style={th}>From</th>
                  <th style={th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((pos, idx) => {
                  const status = craftStatusKey(pos);
                  const st = CRAFT_STATUS_STYLE[status] || CRAFT_STATUS_STYLE.Pending;
                  const sno = (safePage - 1) * POPUP_PAGE_SIZE + idx + 1;
                  return (
                    <tr key={pos.positionId}>
                      <td style={{ ...td, fontWeight: 800 }}>{sno}</td>
                      <td style={td}>
                        <span style={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
                          {pos.positionName || "—"}
                        </span>
                      </td>
                      <td style={td}>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
                          {pos.clientName || "—"}
                        </span>
                      </td>
                      <td style={td}>
                        <span style={{
                          display: "inline-block", padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700,
                          color: "#0041C2", background: "rgba(0,65,194,0.08)", border: "1px solid rgba(0,65,194,0.2)",
                        }}>
                          {pos.jobCode || "—"}
                        </span>
                      </td>
                      <td style={{ ...td, color: "var(--ds-text2)", fontWeight: 600, maxWidth: 160 }}>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }} title={createdByLabel(pos)}>
                          {createdByLabel(pos)}
                        </span>
                      </td>
                      <td style={{ ...td, color: "#0d9488", fontWeight: 800, whiteSpace: "nowrap" }}>
                        {fmtAgingDays(positionAgingDays(pos))}
                      </td>
                      <td style={{ ...td, color: "var(--ds-text2)", fontSize: 12, whiteSpace: "nowrap" }}>
                        {formatAppDate(pos.createdAt) || "—"}
                      </td>
                      <td style={td}>
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px",
                          borderRadius: 999, fontSize: 11, fontWeight: 700, background: st.bg, color: st.color,
                        }}>
                          <span style={{ width: 6, height: 6, borderRadius: "50%", background: st.dot }} />
                          {status === "Closed" ? "Closed" : status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div style={{
          padding: "10px 16px", borderTop: "1px solid var(--ds-border)",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
          flexShrink: 0, background: "var(--ds-card)",
        }}>
          <span style={{ fontSize: 12, color: "var(--ds-muted)", fontWeight: 600 }}>
            {total === 0 ? "Showing 0 of 0" : `Showing ${start}–${end} of ${total}`}
          </span>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <button
              type="button"
              disabled={safePage <= 1 || total === 0}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              style={{ ...actBtn, opacity: safePage <= 1 || total === 0 ? 0.4 : 1 }}
              aria-label="Previous page"
            >
              <ChevronLeft size={14} />
            </button>
            {pageNumbers.map((p, i) => {
              const prev = pageNumbers[i - 1];
              const showEllipsis = prev != null && p - prev > 1;
              return (
                <React.Fragment key={p}>
                  {showEllipsis && (
                    <span style={{ fontSize: 12, color: "var(--ds-muted)", padding: "0 2px" }}>…</span>
                  )}
                  <button
                    type="button"
                    onClick={() => setPage(p)}
                    style={{
                      minWidth: 28, height: 28, borderRadius: 7, border: "none", cursor: "pointer",
                      background: p === safePage ? accent : "var(--ds-card2)",
                      color: p === safePage ? "#fff" : "var(--ds-text2)",
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      fontSize: 12, fontWeight: 800, fontFamily: "inherit",
                    }}
                  >
                    {p}
                  </button>
                </React.Fragment>
              );
            })}
            <button
              type="button"
              disabled={safePage >= totalPages || total === 0}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              style={{ ...actBtn, opacity: safePage >= totalPages || total === 0 ? 0.4 : 1 }}
              aria-label="Next page"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function SelectDropdown({ label, icon: Icon, value, options, open, onToggle, onSelect, accentColor, searchPlaceholder }) {
  const ref = useRef(null);
  const inputRef = useRef(null);
  const [query, setQuery] = useState("");
  const selected = options.find((o) => o.id === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const name = String(o.name || "").toLowerCase();
      const extra = String(o.jobCode || o.email || o.id || "").toLowerCase();
      return name.includes(q) || extra.includes(q);
    });
  }, [options, query]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      return undefined;
    }
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onToggle(false);
    };
    document.addEventListener("mousedown", handler);
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("mousedown", handler);
      clearTimeout(t);
    };
  }, [open, onToggle]);

  return (
    <div style={{ flex: "1 1 180px", minWidth: 160 }} ref={ref}>
      <div style={labelStyle}>
        {Icon && <Icon size={14} />}
        {label}
      </div>
      <div style={{ position: "relative" }}>
        <button
          type="button"
          onClick={() => onToggle(!open)}
          style={{
            ...inp,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            textAlign: "left",
            borderColor: value !== "all" ? accentColor : "var(--ds-border)",
            background: value !== "all" ? `${accentColor}12` : "var(--ds-input-bg)",
            padding: "8px 10px 8px 12px",
          }}
        >
          <span style={{ fontSize: 13, color: value !== "all" ? accentColor : "var(--ds-muted)", fontWeight: value !== "all" ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {selected?.name || "Select…"}
          </span>
          <ChevronDown size={14} style={{ color: "var(--ds-muted)", flexShrink: 0, transition: "transform 0.18s", transform: open ? "rotate(180deg)" : "none" }} />
        </button>
        {open && (
          <div style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 9999,
            background: "var(--ds-card)", border: "1.5px solid var(--ds-border)",
            borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.18)", minWidth: "100%",
            overflow: "hidden",
          }}>
            <div style={{ padding: "8px 10px", borderBottom: "1px solid var(--ds-border)", display: "flex", alignItems: "center", gap: 8 }}>
              <Search size={14} color="var(--ds-muted)" style={{ flexShrink: 0 }} />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder || `Search ${label}…`}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                  flex: 1,
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: 13,
                  color: "var(--ds-text)",
                  fontFamily: "inherit",
                  padding: "4px 0",
                }}
              />
              {query ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); setQuery(""); inputRef.current?.focus(); }}
                  style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--ds-muted)", padding: 0, display: "flex" }}
                >
                  <X size={14} />
                </button>
              ) : null}
            </div>
            <div style={{ maxHeight: 200, overflowY: "auto" }}>
              {filtered.length === 0 ? (
                <div style={{ padding: "12px 14px", fontSize: 12, color: "var(--ds-muted)", fontWeight: 600 }}>
                  No matches
                </div>
              ) : (
                filtered.map((opt) => {
                  const isActive = opt.id === value;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => { onSelect(opt.id); onToggle(false); }}
                      style={{
                        display: "block", width: "100%", padding: "9px 14px",
                        background: isActive ? `${accentColor}12` : "transparent",
                        border: "none", borderBottom: "1px solid var(--ds-border)",
                        cursor: "pointer", textAlign: "left", fontFamily: "inherit",
                        fontSize: 12, fontWeight: isActive ? 700 : 500, color: isActive ? accentColor : "var(--ds-text)",
                      }}
                    >
                      {opt.name}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Multi-select Role/Position with search, checkboxes, and clear (X). Empty selection = all positions. */
function MultiJdSelect({
  label,
  options,
  selectedIds,
  open,
  onToggle,
  onToggleId,
  onClear,
  accentColor,
}) {
  const ref = useRef(null);
  const inputRef = useRef(null);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const name = String(o.name || "").toLowerCase();
      const code = String(o.jobCode || "").toLowerCase();
      const client = String(o.clientName || "").toLowerCase();
      const created = String(o.createdBy || "").toLowerCase();
      const id = String(o.id || "").toLowerCase();
      return name.includes(q) || code.includes(q) || client.includes(q) || created.includes(q) || id.includes(q);
    });
  }, [options, query]);

  const formatOptionLabel = (opt) => {
    if (!opt) return "";
    const code = (opt.jobCode || "").trim();
    return code ? `${opt.name} · ${code}` : opt.name;
  };

  const buttonLabel = selectedIds.size === 0
    ? "All roles"
    : selectedIds.size === 1
      ? (formatOptionLabel(options.find((o) => selectedIds.has(o.id))) || "1 selected")
      : `${selectedIds.size} selected`;

  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onToggle(false);
    };
    document.addEventListener("mousedown", handler);
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("mousedown", handler);
      clearTimeout(t);
    };
  }, [open, onToggle]);

  return (
    <div style={{ flex: "1 1 180px", minWidth: 160 }} ref={ref}>
      <div style={labelStyle}>
        <Briefcase size={14} />
        {label}
      </div>
      <div style={{ position: "relative" }}>
        <button
          type="button"
          onClick={() => onToggle(!open)}
          style={{
            ...inp,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            cursor: "pointer",
            textAlign: "left",
            borderColor: selectedIds.size > 0 ? accentColor : "var(--ds-border)",
            background: selectedIds.size > 0 ? `${accentColor}12` : "var(--ds-input-bg)",
            padding: "8px 10px 8px 12px",
          }}
        >
          <span style={{
            fontSize: 13,
            color: selectedIds.size > 0 ? accentColor : "var(--ds-muted)",
            fontWeight: selectedIds.size > 0 ? 600 : 400,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}>
            {buttonLabel}
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
            {selectedIds.size > 0 && (
              <span
                role="button"
                tabIndex={0}
                title="Clear selection"
                aria-label="Clear JD selection"
                onClick={(e) => { e.stopPropagation(); onClear(); }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onClear(); } }}
                style={{
                  width: 18, height: 18, borderRadius: "50%",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  background: `${accentColor}22`, color: accentColor, cursor: "pointer",
                }}
              >
                <X size={12} />
              </span>
            )}
            <ChevronDown size={14} style={{ color: "var(--ds-muted)", transition: "transform 0.18s", transform: open ? "rotate(180deg)" : "none" }} />
          </span>
        </button>
        {open && (
          <div style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 9999,
            background: "var(--ds-card)", border: "1.5px solid var(--ds-border)",
            borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.18)", overflow: "hidden",
          }}>
                        <div style={{ padding: "8px 10px", borderBottom: "1px solid var(--ds-border)", display: "flex", alignItems: "center", gap: 8 }}>
              <Search size={14} color="var(--ds-muted)" style={{ flexShrink: 0 }} />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search role, client, or code…"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                  flex: 1,
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: 13,
                  color: "var(--ds-text)",
                  fontFamily: "inherit",
                  padding: "4px 0",
                }}
              />
              {query ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); setQuery(""); inputRef.current?.focus(); }}
                  style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--ds-muted)", padding: 0, display: "flex" }}
                >
                  <X size={14} />
                </button>
              ) : null}
            </div>
            {selectedIds.size > 0 && (
              <button
                type="button"
                onClick={onClear}
                style={{
                  display: "block", width: "100%", padding: "8px 14px", background: "transparent",
                  border: "none", borderBottom: "1px solid var(--ds-border)", cursor: "pointer",
                  textAlign: "left", fontSize: 11, fontWeight: 700, color: "#ef4444", fontFamily: "inherit",
                }}
              >
                ✕ Clear selection (show all)
              </button>
            )}
            <div style={{ maxHeight: 220, overflowY: "auto" }}>
              {filtered.length === 0 ? (
                <div style={{ padding: "12px 14px", fontSize: 12, color: "var(--ds-muted)", fontWeight: 600 }}>
                  No matching Role / Position
                </div>
              ) : (
                filtered.map((opt) => {
                  const isChecked = selectedIds.has(opt.id);
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => onToggleId(opt.id)}
                      style={{
                        display: "flex", alignItems: "center", gap: 10, width: "100%",
                        padding: "9px 14px",
                        background: isChecked ? `${accentColor}14` : "transparent",
                        border: "none", borderBottom: "1px solid var(--ds-border)",
                        cursor: "pointer", textAlign: "left", fontFamily: "inherit",
                      }}
                    >
                      <div style={{
                        width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                        border: `2px solid ${isChecked ? accentColor : "var(--ds-border)"}`,
                        background: isChecked ? accentColor : "transparent",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        {isChecked && (
                          <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                            <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: isChecked ? 700 : 500, color: isChecked ? accentColor : "var(--ds-text)", lineHeight: 1.3 }}>
                          {opt.name}
                        </div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--ds-muted)", marginTop: 2 }}>
                          {[opt.jobCode, opt.createdBy && opt.createdBy !== "—" ? `by ${opt.createdBy}` : ""]
                            .filter(Boolean)
                            .join(" · ") || ""}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function JdAnalyticsSection({ selectedTenantIds, tenantOptions = [], onTenantChange }) {
  const defaultTo = useMemo(() => new Date(), []);
  const defaultFrom = useMemo(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d; }, []);
  const todayMaxDate = useMemo(() => toIsoDate(new Date()), []);

  const [from, setFrom] = useState(() => toIsoDate(defaultFrom));
  const [to, setTo] = useState(() => toIsoDate(defaultTo));
  const [selectedClient, setSelectedClient] = useState("all");
  const [selectedCountry, setSelectedCountry] = useState("all");
  /** Empty set = all positions in funnel */
  const [selectedJdIds, setSelectedJdIds] = useState(() => new Set());
  const [clientOpen, setClientOpen] = useState(false);
  const [countryOpen, setCountryOpen] = useState(false);
  const [jdOpen, setJdOpen] = useState(false);
  const [stagePopup, setStagePopup] = useState(null);
  /** Candidates ranking popup for Profile Received → Rejected */
  const [candidatesPopup, setCandidatesPopup] = useState(null);

  const [positions, setPositions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [candidateFunnelCounts, setCandidateFunnelCounts] = useState(null);
  const [decisionRevision, setDecisionRevision] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const abortRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/analytics/positions", { params: tenantQuery(selectedTenantIds) });
        if (cancelled) return;
        setPositions(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setPositions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedTenantIds]);

  const clientOptions = useMemo(() => {
    const names = new Set();
    positions.forEach((p) => {
      const name = (p.clientName || "").trim();
      if (name) names.add(name);
    });
    return [
      { id: "all", name: "All Clients" },
      ...Array.from(names).sort((a, b) => a.localeCompare(b)).map((name) => ({ id: name, name })),
    ];
  }, [positions]);

  const filteredPositions = useMemo(() => {
    return positions.filter((p) => {
      if (selectedClient !== "all") {
        const client = (p.clientName || "").trim();
        if (client !== selectedClient) return false;
      }
      if (selectedCountry !== "all") {
        const country = String(p.country || p.Country || "").trim().toLowerCase();
        if (country && country !== selectedCountry && !country.includes(selectedCountry)) return false;
        // If position has no country field, keep it visible under country filter
      }
      return !p.isDeleted;
    });
  }, [positions, selectedClient, selectedCountry]);

  const jdSelectOptions = useMemo(
    () => filteredPositions.map((p) => ({
      id: String(p.positionId),
      name: p.positionName || `Position ${p.positionId}`,
      jobCode: p.jobCode || "",
      clientName: p.clientName || "",
      createdBy: createdByLabel(p),
      status: p.status || "",
    })),
    [filteredPositions]
  );

  /* Drop selections that are no longer in the filtered list */
  useEffect(() => {
    const valid = new Set(jdSelectOptions.map((o) => o.id));
    setSelectedJdIds((prev) => {
      const next = new Set([...prev].filter((id) => valid.has(id)));
      if (next.size === prev.size && [...next].every((id) => prev.has(id))) return prev;
      return next;
    });
  }, [jdSelectOptions]);

  const effectivePositions = useMemo(() => {
    if (selectedJdIds.size === 0) return filteredPositions;
    return filteredPositions.filter((p) => selectedJdIds.has(String(p.positionId)));
  }, [filteredPositions, selectedJdIds]);

  /** Funnel stays empty until Client, Country, or Role is narrowed (All+All+All roles → all zeros). */
  const hasAnalyticsSelection = useMemo(
    () => selectedClient !== "all" || selectedCountry !== "all" || selectedJdIds.size > 0,
    [selectedClient, selectedCountry, selectedJdIds]
  );

  const loadFunnel = useCallback(async (nextFrom, nextTo, nextSelectedIds, nextFiltered, selectionActive) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const requestId = ++requestIdRef.current;

    setLoading(true);
    setError("");

    if (!selectionActive) {
      setSummary(null);
      setCandidateFunnelCounts(null);
      setLoading(false);
      return;
    }

    const scoped = nextSelectedIds.size === 0
      ? nextFiltered
      : nextFiltered.filter((p) => nextSelectedIds.has(String(p.positionId)));

    const params = { from: nextFrom, to: nextTo, ...tenantQuery(selectedTenantIds) };
    const isPartialScope = nextSelectedIds.size > 0 || nextFiltered.length !== positions.length;
    if (isPartialScope && scoped.length > 0) {
      params.positionIds = scoped.map((p) => p.positionId).join(",");
    } else if (isPartialScope && scoped.length === 0) {
      setSummary(null);
      setCandidateFunnelCounts(null);
      setLoading(false);
      return;
    }

    try {
      const [res, mergedCandidates] = await Promise.all([
        api.get("/analytics/summary", { params, signal: controller.signal }),
        fetchMergedRankingCandidates(scoped),
      ]);
      if (requestId !== requestIdRef.current) return;
      setSummary(res.data || null);
      setCandidateFunnelCounts(countCandidateFunnelStages(mergedCandidates));
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
      if (requestId !== requestIdRef.current) return;
      setError(e?.response?.data?.message || e?.message || "Failed to load JD analytics.");
      setSummary(null);
      setCandidateFunnelCounts(null);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [positions.length, selectedTenantIds]);

  useEffect(() => {
    loadFunnel(from, to, selectedJdIds, filteredPositions, hasAnalyticsSelection);
  }, [from, to, selectedJdIds, filteredPositions, hasAnalyticsSelection, loadFunnel, decisionRevision]);

  const funnelData = useMemo(() => {
    if (!hasAnalyticsSelection) return EMPTY_FUNNEL;
    if (!effectivePositions.length && !summary && !candidateFunnelCounts) return EMPTY_FUNNEL;

    const n = (v) => {
      const x = Number(v ?? 0);
      return Number.isFinite(x) ? x : 0;
    };

    /* Position/Role = positions in current filter; candidate stages match popup ranking lists */
    const positionCreated = effectivePositions.length;
    const profileReceived = n(candidateFunnelCounts?.profileReceived);
    const processed = n(candidateFunnelCounts?.processed);
    const aiShortlisted = n(candidateFunnelCounts?.aiShortlisted);
    const selected = n(candidateFunnelCounts?.selected);
    const rejectedFromCandidates = n(candidateFunnelCounts?.rejected);
    const rejectedFromStatus = effectivePositions.filter((p) => isRejectedStatus(p.status)).length;
    const rejected = Math.max(rejectedFromCandidates, rejectedFromStatus);

    return {
      positionCreated,
      profileReceived,
      processed,
      aiShortlisted,
      selected,
      rejected,
    };
  }, [hasAnalyticsSelection, summary, effectivePositions, candidateFunnelCounts]);

  const funnelStages = FUNNEL_STAGE_META.map((meta, i) => ({
    key: meta.key,
    step: i + 1,
    label: meta.label,
    value: funnelData[meta.key] ?? 0,
    color: meta.color,
  }));

  const conversionCards = [
    {
      icon: TrendingUp,
      iconBg: "#1d4ed8",
      title: "Positions → Profile Pooled",
      fromVal: funnelData.positionCreated,
      toVal: funnelData.profileReceived,
    },
    {
      icon: Target,
      iconBg: "#0d9488",
      title: "AI Shortlisted → Selected",
      fromVal: funnelData.aiShortlisted,
      toVal: funnelData.selected,
    },
    {
      icon: XCircle,
      iconBg: "#dc2626",
      title: "Positions → Rejected",
      fromVal: funnelData.positionCreated,
      toVal: funnelData.rejected,
    },
  ];

  const displayPeriod = formatAppDateRange(from, to);

  const openStagePositionsPopup = useCallback((stage, list) => {
    const rows = Array.isArray(list) ? list : [];
    setCandidatesPopup(null);
    setStagePopup({
      key: stage?.key || "positions",
      title: stage?.label || "Positions",
      accentColor: stage?.color || "#0041C2",
      stageValue: stage?.value,
      subtitle: STAGE_POPUP_HINT[stage?.key] || (
        rows.length === 1
          ? "1 position in current JD filter"
          : `${rows.length} positions in current JD filter`
      ),
      rows,
    });
  }, []);

  const openCandidatesPopup = useCallback((stage, list) => {
    setStagePopup(null);
    setCandidatesPopup({
      key: stage?.key || "candidates",
      title: stage?.label || "Candidates",
      accentColor: stage?.color || "#0041C2",
      stageValue: stage?.value,
      subtitle: STAGE_POPUP_HINT[stage?.key] || "Candidates in the current JD filter.",
      positions: Array.isArray(list) ? list : [],
    });
  }, []);

  const handleStageClick = useCallback((stage) => {
    if (!stage || !hasAnalyticsSelection) return;
    if (CANDIDATE_FUNNEL_KEYS.has(stage.key)) {
      openCandidatesPopup(stage, effectivePositions);
      return;
    }
    openStagePositionsPopup(stage, effectivePositions);
  }, [hasAnalyticsSelection, effectivePositions, openCandidatesPopup, openStagePositionsPopup]);

  const clearJdSelection = () => setSelectedJdIds(new Set());

  const applyPreset = (days) => {
    const t = new Date();
    const f = new Date();
    f.setDate(f.getDate() - days);
    setFrom(toIsoDate(f));
    setTo(toIsoDate(t));
  };

  const toggleJdId = (id) => {
    setSelectedJdIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedChipList = useMemo(() => {
    if (selectedJdIds.size === 0) return [];
    return [...selectedJdIds]
      .map((id) => jdSelectOptions.find((o) => o.id === id))
      .filter(Boolean);
  }, [selectedJdIds, jdSelectOptions]);

  const scopeSubtitle = selectedJdIds.size === 0
    ? `All positions${selectedClient !== "all" ? ` · ${selectedClient}` : ""}${selectedCountry !== "all" ? ` · ${COUNTRY_OPTIONS.find((c) => c.id === selectedCountry)?.name}` : ""}.`
    : selectedJdIds.size === 1
      ? `Pipeline for “${selectedChipList[0]?.name || "selected JD"}” only${selectedChipList[0]?.clientName ? ` · ${selectedChipList[0].clientName}` : ""}.`
      : `Pipeline for ${selectedJdIds.size} selected positions.`;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <FileText size={22} color="#0041C2" />
        <h3 style={{ margin: 0, fontWeight: 900, color: "var(--ds-text)", fontSize: 22 }}>JD Analytics</h3>
      </div>
      <div style={{ color: "var(--ds-text2)", fontSize: 13, marginBottom: 20 }}>
        Funnel for one or more job descriptions — leave Role / Position empty to show all positions. Use checkboxes to multi-select.
      </div>

      <div style={{ ...card, padding: 20, marginBottom: 20 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-end" }}>
          {tenantOptions.length > 0 && (
            <RecruiterSsoDropdown
              label="Tenant"
              icon={Building2}
              allLabel="All Tenants"
              itemNoun="tenants"
              emptyLabel="No tenants found"
              showDetail={false}
              accent="#7c3aed"
              recruiters={tenantOptions.map((t) => ({ email: String(t.id), name: t.name }))}
              selectedEmails={selectedTenantIds instanceof Set ? selectedTenantIds : new Set()}
              onChange={onTenantChange}
            />
          )}
          <SelectDropdown
            label="Client"
            icon={Building2}
            value={selectedClient}
            options={clientOptions}
            open={clientOpen}
            onToggle={setClientOpen}
            onSelect={setSelectedClient}
            accentColor="#0041C2"
          />
          <SelectDropdown
            label="Country"
            icon={Globe}
            value={selectedCountry}
            options={COUNTRY_OPTIONS}
            open={countryOpen}
            onToggle={setCountryOpen}
            onSelect={setSelectedCountry}
            accentColor="#059669"
          />
          <MultiJdSelect
            label="Role / Position"
            options={jdSelectOptions}
            selectedIds={selectedJdIds}
            open={jdOpen}
            onToggle={setJdOpen}
            onToggleId={toggleJdId}
            onClear={clearJdSelection}
            accentColor="#7c3aed"
          />
          <div style={{ flex: "1 1 140px", minWidth: 130 }}>
            <div style={labelStyle}><Calendar size={14} /> From</div>
            <input
              type="date"
              value={from}
              max={to || todayMaxDate}
              onChange={(e) => setFrom(e.target.value)}
              style={inp}
            />
          </div>
          <div style={{ flex: "1 1 140px", minWidth: 130 }}>
            <div style={labelStyle}><Calendar size={14} /> To</div>
            <input
              type="date"
              value={to}
              min={from}
              max={todayMaxDate}
              onChange={(e) => setTo(e.target.value)}
              style={inp}
            />
          </div>
          <div style={{ flex: "1 1 280px", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {DATE_PRESETS.map((p) => {
              const activeTo = toIsoDate(new Date());
              const activeFromDate = new Date();
              activeFromDate.setDate(activeFromDate.getDate() - p.days);
              const activeFrom = toIsoDate(activeFromDate);
              const isActive = from === activeFrom && to === activeTo;
              return (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => applyPreset(p.days)}
                  disabled={loading}
                  style={{
                    ...btnOutline,
                    flex: "1 1 80px",
                    opacity: loading ? 0.6 : 1,
                    borderColor: isActive ? "#0041C2" : "var(--ds-border)",
                    background: isActive ? "rgba(0,65,194,0.1)" : "var(--ds-input-bg)",
                    color: isActive ? "#0041C2" : "var(--ds-text2)",
                    fontWeight: isActive ? 700 : 600,
                  }}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 11, color: "var(--ds-muted)", fontWeight: 600 }}>
            {loading
              ? "Loading funnel…"
              : !hasAnalyticsSelection
                ? "Select a Client, Country, or Role / Position to load funnel details"
                : `Showing funnel for ${displayPeriod}`}
          </span>
          {selectedJdIds.size === 0 && (
            <span style={{ background: "rgba(124,58,237,0.1)", border: "1px solid rgba(124,58,237,0.3)", color: "#7c3aed", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600 }}>
              All positions
            </span>
          )}
          {selectedChipList.map((chip) => (
            <span
              key={chip.id}
              style={{
                background: "rgba(124,58,237,0.1)", border: "1px solid rgba(124,58,237,0.3)",
                color: "#7c3aed", borderRadius: 20, padding: "3px 8px 3px 10px", fontSize: 11, fontWeight: 600,
                display: "inline-flex", alignItems: "center", gap: 4,
              }}
            >
              {chip.name}
              {(chip.jobCode || "").trim() ? ` · ${chip.jobCode}` : ""}
              <button
                type="button"
                title="Remove"
                onClick={() => toggleJdId(chip.id)}
                style={{
                  width: 16, height: 16, borderRadius: "50%", border: "none", padding: 0,
                  background: "rgba(124,58,237,0.2)", color: "#7c3aed", cursor: "pointer",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <X size={10} />
              </button>
            </span>
          ))}
          {selectedClient !== "all" && (
            <span style={{ background: "rgba(0,65,194,0.1)", border: "1px solid rgba(0,65,194,0.25)", color: "#0041C2", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600 }}>
              {selectedClient}
            </span>
          )}
          {selectedCountry !== "all" && (
            <span style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.3)", color: "#059669", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600 }}>
              {COUNTRY_OPTIONS.find((c) => c.id === selectedCountry)?.name}
            </span>
          )}
        </div>
      </div>

      <div style={{ ...card, padding: 20, marginBottom: 16 }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 900, color: "var(--ds-text)", fontSize: 16 }}>JD Analytics</div>
          <div style={{ color: "var(--ds-text2)", fontSize: 12, marginTop: 4, maxWidth: 520 }}>
            {scopeSubtitle}
          </div>
        </div>

        {error ? (
          <div style={{ ...card2, padding: 24, textAlign: "center", color: "#ef4444", fontSize: 13, fontWeight: 600 }}>
            {error}
          </div>
        ) : filteredPositions.length === 0 ? (
          <div style={{ ...card2, padding: 24, textAlign: "center", color: "var(--ds-muted)", fontSize: 13, fontWeight: 600 }}>
            No positions match the selected filters.
          </div>
        ) : (
          <>
            <HiringReachFunnel stages={funnelStages} onStageClick={hasAnalyticsSelection ? handleStageClick : undefined} />
            <div style={{ color: "var(--ds-muted)", fontSize: 11, fontWeight: 600, marginTop: 10 }}>
              Tip: Positions opens positions; Profile Pooled → Rejected opens ranked candidates. Use Selection Status on Profile Pooled / Processed to mark Selected or Rejected.
            </div>
            <div style={{ display: "flex", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
              {conversionCards.map((c) => (
                <ConversionCard key={c.title} {...c} />
              ))}
            </div>
          </>
        )}
      </div>

      {stagePopup && (
        <FunnelStagePositionsPopup
          rows={stagePopup.rows || []}
          title={stagePopup.title}
          subtitle={stagePopup.subtitle}
          accentColor={stagePopup.accentColor}
          stageValue={stagePopup.stageValue}
          onClose={() => setStagePopup(null)}
        />
      )}

      {candidatesPopup && (
        <FunnelCandidatesPopup
          positions={candidatesPopup.positions || []}
          stageKey={candidatesPopup.key}
          title={candidatesPopup.title}
          subtitle={candidatesPopup.subtitle}
          accentColor={candidatesPopup.accentColor}
          stageValue={candidatesPopup.stageValue}
          onClose={() => setCandidatesPopup(null)}
          onDecisionChange={() => setDecisionRevision((n) => n + 1)}
        />
      )}
    </div>
  );
}
